import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getAuthHeader, serverUrl } from ".";
import { PaginationType } from "../types/filter";

export const useAnnouncement = ({
  pagination,
}: {
  pagination: PaginationType;
}) =>
  useQuery({
    queryKey: ["announcement", pagination],
    queryFn: async () => {
      const res = await axios.get(
        `${serverUrl}/notice/announcement?${new URLSearchParams({
          start: (pagination.page * pagination.size).toString(),
          limit: pagination.size.toString(),
        }).toString()}`,
        getAuthHeader()
      );
      return res.data;
    },
  });

export const useAnnouncementDetail = ({ id }: { id: string }) =>
  useQuery({
    queryKey: ["announcement", id],
    queryFn: async () => {
      const res = await axios.get(
        `${serverUrl}/notice/announcement/${id}`,
        getAuthHeader()
      );
      return res.data;
    },
    enabled: id !== undefined,
  });

export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await axios.delete(
        `${serverUrl}/notice/announcement/${id}`,
        getAuthHeader()
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement"] });
    },
  });
};

export const usePatchAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await axios.patch(
        `${serverUrl}/notice/announcement/${id}`,
        data,
        getAuthHeader()
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement"] });
    },
  });
};

export const useAddAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const formData = new FormData();

      formData.append("title", data.title);
      formData.append("content", data.content);
      formData.append("department", JSON.stringify(data.department));
      if (data.files) {
        Array.from(data.files as File[]).forEach((file) => {
          formData.append("files", file);
        });
      }
      await axios.post(
        `${serverUrl}/notice/announcement`,
        formData,
        getAuthHeader()
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement"] });
    },
  });
};

export const usePostInbox = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      await axios.post(`${serverUrl}/notice/inbox`, data, getAuthHeader());
    },
  });
};

export const useInbox = () => {
  return useQuery({
    queryKey: ["inbox"],
    queryFn: async () => {
      const res = await axios.get(`${serverUrl}/notice/inbox`, getAuthHeader());
      return res.data;
    },
  });
};

export const useInboxDetail = ({ id }: { id: string }) => {
  return useQuery({
    queryKey: ["inbox", id],
    queryFn: async () => {
      const res = await axios.get(
        `${serverUrl}/notice/inbox/${id}`,
        getAuthHeader()
      );
      return res.data;
    },
    enabled: id !== undefined,
  });
};

export const useReadInbox = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await axios.patch(
        `${serverUrl}/notice/inbox/${id}`,
        { read: true },
        getAuthHeader()
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
  });
};

export const useFaQList = () => {
  return useQuery({
    queryKey: ["faq"],
    queryFn: async () => {
      const res = await axios.get(`${serverUrl}/notice/faq`, getAuthHeader());
      return res.data;
    },
  });
};

export const useCreateFaQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      await axios.post(`${serverUrl}/notice/faq`, data, getAuthHeader());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
    },
  });
};

export const usePatchFaQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      await axios.patch(`${serverUrl}/notice/faq/${id}`, data, getAuthHeader());
    },
    onSuccess: () => {
      alert("성공적으로 수정되었습니다.");
      queryClient.invalidateQueries({ queryKey: ["faq"] });
    },
  });
};

export const useDeleteFaQ = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      await axios.delete(`${serverUrl}/notice/faq/${id}`, getAuthHeader());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
    },
  });
};
